import { useQuery } from '@tanstack/react-query';
import { AssetModel, EquipmentPartModel } from '../data/types';
import { inspectionDB } from '../data/database.structure';
import DatabaseAssetsMethods from '../data/database.assets.methods';
import { PartName } from '../data/enums';
import { queryKeys } from '../store/queryKeys';

interface AssetEquipmentPartState {
  equipmentPart?: EquipmentPartModel | null;
  numberOfShoes?: number | null;
  asset?: AssetModel | null;
  isLoading: boolean;
  error?: string;
}

interface AssetEquipmentPartProps {
  externalId?: string;
  asset?: AssetModel;
  partCategory?: string;
  sideName?: string;
  partName: PartName;
}

export const useAssetEquipmentPartCached = ({
  externalId,
  asset,
  sideName,
  partName,
  partCategory,
}: AssetEquipmentPartProps) => {
  const {
    data: result,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [
      'assetEquipmentPart',
      externalId,
      sideName,
      partName,
      partCategory,
    ],
    queryFn: async () => {
      if (!externalId) {
        throw new Error('Inspection not found');
      }

      const assetMethods = new DatabaseAssetsMethods(inspectionDB);

      if (!asset) {
        const a = await assetMethods.getAssetByInspectionId(externalId);
        asset = a ?? undefined;
        if (!asset) {
          throw new Error('Asset not found');
        }
      }

      if (asset.equipments.length == 0) {
        const equipments = await assetMethods.getAssetEquipment(asset.id);
        asset.equipments = equipments;
      }

      const sideNameDefined = sideName
        ?.split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      const partCategoryDefined =
        partCategory
          ?.split('-')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ') ?? '';

      if (partCategoryDefined === '') {
        console.log('part category empty');
      }

      const equipment = asset.equipments.find((equipment) => {
        return equipment.name === sideNameDefined;
      });

      if (!equipment) {
        throw new Error('Equipment not found');
      }

      const equipmentPart = await assetMethods.getAssetEquipmentPart(
        equipment.id,
        partCategoryDefined,
        partName,
      );

      return {
        equipmentPart,
        numberOfShoes: asset.model.shoesPerSide ?? 5,
        asset,
      };
    },
    enabled: !!externalId,
    // Cache equipment part data for a reasonable time since it doesn't change often
    staleTime: 8 * 60 * 1000, // 8 minutes
    gcTime: 20 * 60 * 1000, // 20 minutes
    // Retry on failure
    retry: 2,
    networkMode: 'always',
  });

  const state: AssetEquipmentPartState = {
    equipmentPart: result?.equipmentPart,
    numberOfShoes: result?.numberOfShoes,
    asset: result?.asset,
    isLoading,
    error: error?.message,
  };

  return { ...state, refetch };
};
